import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ImageDropzone.css';

interface ImageDropzoneProps {
	/** Currently staged image files. */
	files: File[];
	/** Called with the next full file list (add/remove). */
	onFilesChange: (files: File[]) => void;
	/** Disable all interaction (e.g. while a run is in flight). */
	disabled?: boolean;
}

const isImage = (f: File | null): f is File => !!f && f.type.startsWith('image/');

/** One staged image: object-URL thumbnail with a remove button. */
function ThumbItem({ file, onRemove }: { file: File; onRemove: () => void }) {
	const url = useMemo(() => URL.createObjectURL(file), [file]);
	useEffect(() => () => URL.revokeObjectURL(url), [url]);
	return (
		// stopPropagation so clicking a thumb (or its ✕) never re-opens the file dialog.
		<div className="dz-thumb" onClick={(e) => e.stopPropagation()}>
			<img src={url} alt={file.name || 'pasted image'} />
			<button className="dz-thumb-x" type="button" onClick={onRemove} title="Remove">
				✕
			</button>
			<span className="dz-thumb-name">{file.name || 'pasted'}</span>
		</div>
	);
}

/**
 * Image input that accepts drag-&-drop, clipboard paste, and click-to-browse.
 *
 * Paste fires via the element's onPaste, so the zone must be focused first
 * (click it, or tab to it) — this keeps paste scoped to one dropzone even when
 * several panels are open. Drag-&-drop and Browse work without focus.
 */
export function ImageDropzone({ files, onFilesChange, disabled = false }: ImageDropzoneProps) {
	const [dragOver, setDragOver] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const addFiles = useCallback(
		(incoming: File[]) => {
			const imgs = incoming.filter(isImage);
			if (imgs.length) onFilesChange([...files, ...imgs]);
		},
		[files, onFilesChange],
	);

	const removeAt = useCallback(
		(idx: number) => onFilesChange(files.filter((_, i) => i !== idx)),
		[files, onFilesChange],
	);

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setDragOver(false);
			if (!disabled) addFiles(Array.from(e.dataTransfer.files));
		},
		[addFiles, disabled],
	);

	const onPaste = useCallback(
		(e: React.ClipboardEvent) => {
			if (disabled) return;
			const imgs = Array.from(e.clipboardData.items)
				.filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
				.map((it) => it.getAsFile())
				.filter(isImage);
			if (imgs.length) {
				e.preventDefault();
				addFiles(imgs);
			}
		},
		[addFiles, disabled],
	);

	const browse = useCallback(() => {
		if (!disabled) inputRef.current?.click();
	}, [disabled]);

	return (
		<div
			className={`dropzone${dragOver ? ' over' : ''}${disabled ? ' disabled' : ''}`}
			tabIndex={disabled ? -1 : 0}
			role="button"
			aria-label="Add screenshots: drag and drop, paste, or browse"
			onPaste={onPaste}
			onDragOver={(e) => {
				e.preventDefault();
				if (!disabled) setDragOver(true);
			}}
			onDragLeave={() => setDragOver(false)}
			onDrop={onDrop}
		>
			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				multiple
				hidden
				onChange={(e) => {
					addFiles(Array.from(e.target.files ?? []));
					e.target.value = ''; // allow re-selecting the same file
				}}
			/>

			{files.length === 0 ? (
				<div className="dz-empty">
					<div className="dz-icon">⤓</div>
					<div className="dz-title">Drop, paste, or browse for screenshots</div>
					<div className="dz-sub">
						Drag &amp; drop · click here then <kbd>⌘/Ctrl</kbd>+<kbd>V</kbd> · or
						<button type="button" className="dz-browse" onClick={browse} disabled={disabled}>
							Browse
						</button>
					</div>
				</div>
			) : (
				<div className="dz-files">
					{files.map((f, i) => (
						<ThumbItem key={`${f.name}-${i}`} file={f} onRemove={() => removeAt(i)} />
					))}
					<button
						type="button"
						className="dz-add"
						title="Add more (drop, paste, or browse)"
						onClick={(e) => {
							e.stopPropagation();
							browse();
						}}
						disabled={disabled}
					>
						+ Add
					</button>
				</div>
			)}
		</div>
	);
}
